import { Avatar, makeStyles, tokens } from '@fluentui/react-components';
import { Person20Regular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../../hooks/useAuth/useAuth.js';
import {
  getGraphProfilePhoto,
  getGraphUserDisplayName,
} from '../../services/profilePhotosService.js';

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
    gap: '8px',
    minWidth: 0,
  },
  name: { overflowWrap: 'anywhere', fontSize: tokens.fontSizeBase200 },
});

export function RunCreator({ createdBy }: { createdBy: string | null }) {
  const styles = useStyles();
  const { account, getGraphToken } = useAuth();
  const creator = createdBy?.trim() ?? '';
  const opaque =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      creator,
    );
  const { data } = useQuery({
    queryKey: ['evaluationCreatorProfile', account?.homeAccountId, creator],
    enabled: Boolean(creator && account),
    staleTime: 30 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const [name, photo] = await Promise.allSettled([
        getGraphUserDisplayName(getGraphToken, creator),
        getGraphProfilePhoto(getGraphToken, creator),
      ]);
      const unavailable =
        name.status === 'rejected' || photo.status === 'rejected';
      if (unavailable)
        console.warn('Evaluation creator profile lookup unavailable.');
      return {
        name: name.status === 'fulfilled' ? name.value : undefined,
        photo: photo.status === 'fulfilled' ? photo.value : undefined,
        unavailable,
      };
    },
  });
  const name = !creator
    ? 'Not recorded'
    : (data?.name ?? (opaque ? 'Unresolved identity' : creator));
  const unresolved = !creator || (opaque && !data?.name);
  return (
    <span
      className={styles.root}
      title={`Created by: ${creator || 'Not recorded'}${data?.unavailable ? '\nProfile information unavailable' : ''}`}
    >
      <Avatar
        size={28}
        color={unresolved ? 'neutral' : 'colorful'}
        name={unresolved ? undefined : name}
        image={data?.photo ? { src: data.photo } : undefined}
        icon={unresolved ? <Person20Regular /> : undefined}
        aria-hidden
      />
      <span className={styles.name}>{name}</span>
    </span>
  );
}
